import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Users, Bot, Rss, Upload, MessageSquare, Lightbulb, User, LogOut, ClipboardCheck } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Bottom navigation item with simple Lucide icon
const BottomNavItem = ({ to, icon: Icon, label }) => (
  <NavLink
    to={to}
    className={({ isActive }) =>
      `flex flex-col items-center gap-1 px-4 py-2 transition-all ${
        isActive ? 'text-zom-yellow' : 'text-zom-white/60 hover:text-zom-white'
      }`
    }
  >
    <Icon className="w-5 h-5" strokeWidth={2.5} />
    <span className="text-[10px] font-heading tracking-wider">{label}</span>
  </NavLink>
);

// Top navigation item with simple Lucide icon
const TopNavItem = ({ to, icon: Icon, label, badge, onClick }) => {
  const content = (
    <>
      <Icon className="w-4 h-4" strokeWidth={2.5} />
      <span className="text-[8px] font-heading tracking-wide">{label}</span>
      {badge > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[12px] h-3 bg-zom-red border border-zom-black text-[7px] flex items-center justify-center text-zom-white font-heading px-0.5">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="relative flex flex-col items-center gap-0.5 px-2 py-1 transition-all text-zom-white/60 hover:text-zom-white"
      >
        {content}
      </button>
    );
  }

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `relative flex flex-col items-center gap-0.5 px-2 py-1 transition-all ${
          isActive ? 'text-zom-yellow' : 'text-zom-white/60 hover:text-zom-white'
        }`
      }
    >
      {content}
    </NavLink>
  );
};

const MobileNav = ({ unreadCount = 0 }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = () => {
    auth?.logout();
    navigate('/login');
    setShowLogoutDialog(false);
  };

  if (!auth?.user) return null;

  const { user } = auth;
  const userRoles = user.roles || [user.role] || [];
  const isAdminUser = userRoles.includes('admin');
  const isWriterUser = userRoles.includes('writer') || isAdminUser;
  const isPerformer = userRoles.includes('performer');
  // Guest-only: has guest role but NOT performer, writer, or admin
  const isGuestOnly = !isPerformer && !isWriterUser && !isAdminUser;

  // Guest-only sees minimal UI: Feed and Out only
  if (isGuestOnly) {
    return (
      <>
        {/* Top Bar - just logout for guests */}
        <nav className="fixed top-0 left-0 right-0 bg-zom-black border-b-2 border-zom-yellow z-50">
          <div className="flex items-center justify-center py-2">
            <button
              onClick={handleLogoutClick}
              className="flex flex-col items-center gap-1 px-3 py-2 text-zom-white/60 hover:text-zom-red transition-all"
            >
              <LogOut className="w-5 h-5" strokeWidth={2.5} />
              <span className="text-[10px] font-heading tracking-wider">OUT</span>
            </button>
          </div>
        </nav>

        {/* Bottom Nav - Feed only */}
        <nav className="fixed bottom-0 left-0 right-0 bg-zom-black border-t-2 border-zom-yellow z-50">
          <div className="flex justify-center items-center py-1">
            <BottomNavItem to="/" icon={Rss} label="FEED" />
          </div>
        </nav>

        {/* Logout Confirmation */}
        <LogoutDialog 
          open={showLogoutDialog} 
          onOpenChange={setShowLogoutDialog}
          onConfirm={handleLogoutConfirm}
        />
      </>
    );
  }

  return (
    <>
      {/* TOP NAVIGATION BAR - Single row for all users */}
      <nav className="fixed top-0 left-0 right-0 bg-zom-black border-b-2 border-zom-yellow z-50">
        <div className="flex items-center justify-center gap-2 px-2 py-1">
          <TopNavItem to="/inspirations" icon={Lightbulb} label="PROMPTS" />
          <TopNavItem to="/centrum" icon={User} label="CENTRUM" badge={unreadCount} />
          
          {/* Writer gets REVIEW */}
          {isWriterUser && (
            <TopNavItem to="/review" icon={ClipboardCheck} label="REVIEW" />
          )}
          
          {/* Admin gets USERS and BOT */}
          {isAdminUser && (
            <>
              <TopNavItem to="/admin/users" icon={Users} label="USERS" />
              <TopNavItem to="/admin/zombiebot" icon={Bot} label="BOT" />
            </>
          )}
          
          <TopNavItem onClick={handleLogoutClick} icon={LogOut} label="OUT" />
        </div>
      </nav>

      {/* BOTTOM NAVIGATION BAR */}
      <nav className="fixed bottom-0 left-0 right-0 bg-zom-black border-t-2 border-zom-yellow z-50">
        <div className="flex justify-center items-center gap-8 py-1">
          <BottomNavItem to="/" icon={Rss} label="FEED" />
          <BottomNavItem to="/upload" icon={Upload} label="UPLOAD" />
          <BottomNavItem to="/forum" icon={MessageSquare} label="FORUM" />
        </div>
      </nav>

      {/* Logout Confirmation Dialog */}
      <LogoutDialog 
        open={showLogoutDialog} 
        onOpenChange={setShowLogoutDialog}
        onConfirm={handleLogoutConfirm}
      />
    </>
  );
};

// Logout confirmation dialog component
const LogoutDialog = ({ open, onOpenChange, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent className="bg-zom-black border-3 border-zom-yellow text-zom-white">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-heading text-zom-yellow tracking-wider">
          WYLOGOWAĆ?
        </AlertDialogTitle>
        <AlertDialogDescription className="text-zom-white/70 font-body">
          Czy na pewno chcesz się wylogować?
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="bg-zom-black border-2 border-zom-white text-zom-white hover:bg-zom-white hover:text-zom-black font-heading">
          ANULUJ
        </AlertDialogCancel>
        <AlertDialogAction
          onClick={onConfirm}
          className="bg-zom-red hover:bg-zom-red/80 text-zom-white font-heading border-2 border-zom-black"
        >
          WYLOGUJ
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default MobileNav;
